/**
 * Property History API — React port of propertyHistoryService.js
 *
 * Source: public/SPIGlassApp/services/propertyHistoryService.js
 *
 * The AngularJS service exposed a single `refreshPropertyHistory(propertyId)`
 * function that hit `/API/WorkOrderInfo/GetPropertyHistorySummary` and stored
 * the response as `propertyHistory.workOrders` on the service singleton.
 *
 * In React we just call the API directly — consumers manage their own state
 * via React Query or local state.
 */

import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';

const API_BASE = `${buildApiBase()}/API/WorkOrderInfo`;

export interface PropertyHistoryWorkOrder {
  orderNumber?: string | number;
  clientCode?: string;
  workCode?: string;
  orderStatus?: string;
  orderDate?: string;
  dueDate?: string;
  completeDate?: string;
  billTo?: string;
  orderBy?: string;
  vendorCode?: string;
  invoiceNumber?: string;
  invoiceAmount?: number;
  vendorCost?: number;
  department?: string;
  [key: string]: unknown;
}

export interface PropertyHistorySummary {
  workOrders?: PropertyHistoryWorkOrder[];
  [key: string]: unknown;
}

/**
 * Fetch the property history summary for a property.
 *
 * Port of propertyHistoryService.refreshPropertyHistory(propertyId) from
 * public/SPIGlassApp/services/propertyHistoryService.js (lines 10-19).
 *
 * The AngularJS service stored the summary at `propertyHistory` and resolved
 * with `response.data.workOrders`. This function returns the full summary so
 * callers can use either piece.
 */
export async function getPropertyHistorySummary(
  spiPropertyId: number | string
): Promise<PropertyHistorySummary> {
  const response = await axiosClient.get<PropertyHistorySummary>(
    `${API_BASE}/GetPropertyHistorySummary`,
    { params: { spiPropertyId } }
  );
  return response.data ?? {};
}

/**
 * Fetch only the `workOrders` array from the property history summary.
 * Convenience wrapper for callers that only need the list.
 */
export async function getPropertyHistoryWorkOrders(
  spiPropertyId: number | string
): Promise<PropertyHistoryWorkOrder[]> {
  const summary = await getPropertyHistorySummary(spiPropertyId);
  return summary.workOrders ?? [];
}
